// src/components/contacts/ContactMessaging.jsx
import React, { useState } from 'react';
import { FiX, FiSend, FiMessageSquare, FiLinkedin } from 'react-icons/fi';
import { Sparkles } from 'lucide-react';
import { contactsService } from '../../utils/contactsUtils';
import { useAuth } from '../../context/AuthContext';

const ContactMessaging = ({ contacts, onClose, onMessageSent }) => {
  const { userProfile } = useAuth();
  const organizationId = userProfile?.organizationId;
  
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  // Filter contacts that have LinkedIn profiles
  const linkedinContacts = contacts.filter(contact => contact.linkedin);

  const handleSendMessages = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (linkedinContacts.length === 0) {
      setError('No contacts with LinkedIn profiles selected');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const messageData = {
        subject: subject.trim(),
        message: message.trim(),
        contacts: linkedinContacts.map(contact => ({
          id: contact.id,
          name: contact.name,
          linkedin: contact.linkedin
        })),
        sentBy: userProfile?.uid,
        senderName: userProfile?.displayName || userProfile?.email
      };

      await contactsService.sendLinkedInMessages(organizationId, messageData);
      onMessageSent();
    } catch (err) {
      setError('Failed to send LinkedIn messages');
      console.error('Error sending LinkedIn messages:', err);
    } finally {
      setSending(false);
    }
  };

  const handleAIGeneration = async () => {
    if (!aiPrompt.trim()) {
      setError('Please enter a prompt for AI message generation');
      return;
    }

    try {
      setAiGenerating(true);
      setError(null);
      
      const generatedContent = await contactsService.generateLinkedInMessage(aiPrompt, {
        recipientNames: linkedinContacts.map(c => c.name),
        senderName: userProfile?.displayName || userProfile?.email,
        organizationName: userProfile?.organizationName
      });
      
      setMessage(generatedContent.message);
      if (generatedContent.subject) {
        setSubject(generatedContent.subject);
      }
      setShowAiPanel(false);
      setAiPrompt('');
    } catch (err) {
      setError('Failed to generate AI message');
      console.error('Error generating AI message:', err);
    } finally {
      setAiGenerating(false);
    }
  };

  const getAIPromptSuggestions = () => [
    'Write a professional connection request',
    'Draft a follow-up message after meeting at a conference',
    'Create a networking message for potential collaboration',
    'Write a message to reconnect with an old colleague',
    'Draft an introduction message with business proposal'
  ];

  if (linkedinContacts.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">LinkedIn Messaging</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <FiX size={18} />
            </button>
          </div>
          
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
              <FiLinkedin className="text-yellow-600" size={24} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No LinkedIn Profiles</h3>
            <p className="text-gray-600 mb-6">
              None of the selected contacts have LinkedIn profiles. Please add LinkedIn URLs to contacts or select different contacts.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <FiLinkedin className="text-blue-600 mr-2" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Send LinkedIn Messages</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <FiX size={18} />
          </button>
        </div>

        {/* Recipients */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Recipients ({linkedinContacts.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {linkedinContacts.map(contact => (
              <span 
                key={contact.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
              >
                <FiLinkedin className="mr-1" size={12} />
                {contact.name}
              </span>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="space-y-4">
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject (Optional)
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Message subject..."
              />
            </div>

            {/* AI Assistant Toggle */}
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700">
                Message
              </label>
              <button
                onClick={() => setShowAiPanel(!showAiPanel)}
                className="px-3 py-1 text-sm bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 flex items-center"
              >
                <Sparkles className="mr-1" size={14} />
                AI Assistant
              </button>
            </div>

            {/* AI Panel */}
            {showAiPanel && (
              <div className="border border-purple-200 rounded-lg p-4 bg-gradient-to-r from-purple-50 to-blue-50">
                <h4 className="text-sm font-medium text-purple-800 mb-2">
                  AI Message Generator
                </h4>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  rows={2}
                  placeholder="Describe the message you want to send..."
                />
                
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="text-xs text-gray-500">Quick suggestions:</span>
                  {getAIPromptSuggestions().map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setAiPrompt(suggestion)}
                      className="text-xs px-2 py-1 bg-white border border-gray-200 rounded-full hover:bg-purple-50 hover:border-purple-200"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
                
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={handleAIGeneration}
                    disabled={aiGenerating || !aiPrompt.trim()}
                    className="px-3 py-1 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center"
                  >
                    {aiGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-1" size={12} />
                        Generate
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={8}
                placeholder="Write your LinkedIn message here..."
              />
              <div className="mt-1 text-xs text-gray-500">
                {message.length}/300 characters recommended
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Note: These messages will be logged but not automatically sent through LinkedIn. 
            You'll need to copy and send them manually.
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSendMessages}
              disabled={sending || !message.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving Messages...
                </>
              ) : (
                <>
                  <FiSend className="mr-2" size={16} />
                  Save Messages
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactMessaging;